import socketService from './SocketService';
import httpClient from '../httpClient';

// ===== Types =====

export interface Ticket {
  id: number;
  ticket_number: string;
  subject: string;
  description?: string;
  category: 'technical' | 'account' | 'billing' | 'course' | 'general';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'open' | 'assigned' | 'in_progress' | 'pending_user' | 'resolved' | 'closed';
  user_email?: string;
  assigned_agent_email?: string;
  reply_count?: number;
  unread_replies?: number;
  created_at: Date;
  updated_at: Date;
  resolved_at?: Date;
  closed_at?: Date;
}

export interface TicketReply {
  id: number;
  message: string;
  user_type: 'user' | 'agent';
  user_email: string;
  is_internal: boolean;
  created_at: Date;
}

export interface TicketDetails extends Ticket {
  replies: TicketReply[];
}

export interface CreateTicketData {
  subject: string;
  description: string;
  category?: 'technical' | 'account' | 'billing' | 'course' | 'general';
  priority?: 'low' | 'medium' | 'high' | 'urgent';
}

export interface TicketUpdate {
  ticketId: number;
  updateType: 'status_change' | 'new_reply' | 'assigned';
  data: any;
  timestamp: Date;
}

// ===== Ticket Service =====

class TicketService {
  private ticketUpdateCallbacks: Map<number, (update: TicketUpdate) => void> = new Map();

  /**
   * Create a new support ticket
   * @param ticketData - Ticket data
   * @returns Promise with created ticket
   */
  async createTicket(ticketData: CreateTicketData): Promise<Ticket> {
    try {
      const response = await httpClient.post<{ success: boolean; ticket: Ticket }>(
        '/support/tickets',
        ticketData
      );

      if (response.success && response.ticket) {
        return response.ticket;
      }

      throw new Error('Failed to create ticket');
    } catch (error: any) {
      console.error('Error creating ticket:', error);
      throw error;
    }
  }

  /**
   * Get user's tickets
   * @param status - Optional status filter
   * @returns Promise with list of tickets
   */
  async getUserTickets(status?: string): Promise<Ticket[]> {
    try {
      const url = status ? `/support/tickets?status=${status}` : '/support/tickets';
      const response = await httpClient.get<{ success: boolean; tickets: Ticket[] }>(url);
      return response.tickets || [];
    } catch (error: any) {
      console.error('Error fetching tickets:', error);
      throw error;
    }
  }

  /**
   * Get ticket details with all replies
   * @param ticketId - Ticket ID
   * @returns Promise with ticket details
   */
  async getTicketDetails(ticketId: number): Promise<TicketDetails> {
    try {
      const response = await httpClient.get<{ success: boolean; ticket: TicketDetails }>(
        `/support/tickets/${ticketId}`
      );

      if (response.success && response.ticket) {
        return response.ticket;
      }

      throw new Error('Ticket not found');
    } catch (error: any) {
      console.error('Error fetching ticket details:', error);
      throw error;
    }
  }

  /**
   * Reply to a ticket
   * @param ticketId - Ticket ID
   * @param message - Reply message
   * @returns Promise with reply data
   */
  async addTicketReply(ticketId: number, message: string): Promise<TicketReply> {
    try {
      const response = await httpClient.post<{ success: boolean; reply: TicketReply }>(
        `/support/tickets/${ticketId}/reply`,
        { message }
      );

      if (response.success && response.reply) {
        return response.reply;
      }

      throw new Error('Failed to add reply');
    } catch (error: any) {
      console.error('Error adding ticket reply:', error);
      throw error;
    }
  }

  /**
   * Close a ticket
   * @param ticketId - Ticket ID
   */
  async closeTicket(ticketId: number): Promise<void> {
    try {
      const response = await httpClient.patch<{ success: boolean; message: string }>(
        `/support/tickets/${ticketId}/close`
      );

      if (!response.success) {
        throw new Error(response.message || 'Failed to close ticket');
      }
    } catch (error: any) {
      console.error('Error closing ticket:', error);
      throw error;
    }
  }

  /**
   * Subscribe to real-time updates for a ticket
   * @param ticketId - Ticket ID
   * @param callback - Callback to handle updates
   */
  subscribeToTicket(ticketId: number, callback: (update: TicketUpdate) => void): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!socketService.isConnected()) {
        reject(new Error('Socket not connected'));
        return;
      }

      socketService.emit(
        'ticket:subscribe',
        { ticketId },
        (response: { success: boolean; ticketNumber?: string; message?: string }) => {
          if (response.success) {
            // Store callback for this ticket
            this.ticketUpdateCallbacks.set(ticketId, callback);

            // Listen for updates
            if (!socketService.isConnected()) return;

            socketService.on('ticket:update', this.handleTicketUpdate.bind(this));

            resolve();
          } else {
            reject(new Error(response.message || 'Failed to subscribe to ticket'));
          }
        }
      );
    });
  }

  /**
   * Unsubscribe from ticket updates
   * @param ticketId - Ticket ID
   */
  unsubscribeFromTicket(ticketId: number): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!socketService.isConnected()) {
        reject(new Error('Socket not connected'));
        return;
      }

      socketService.emit(
        'ticket:unsubscribe',
        { ticketId },
        (response: { success: boolean; message?: string }) => {
          if (response.success) {
            // Remove callback
            this.ticketUpdateCallbacks.delete(ticketId);

            // If no more subscriptions, remove listener
            if (this.ticketUpdateCallbacks.size === 0) {
              socketService.off('ticket:update');
            }

            resolve();
          } else {
            reject(new Error(response.message || 'Failed to unsubscribe from ticket'));
          }
        }
      );
    });
  }

  /**
   * Handle ticket update events
   * @param update - Ticket update data
   */
  private handleTicketUpdate(update: TicketUpdate): void {
    const callback = this.ticketUpdateCallbacks.get(update.ticketId);
    if (callback) {
      callback(update);
    }
  }

  /**
   * Listen for new ticket notifications (admin only)
   * @param callback - Callback to handle new tickets
   */
  onNewTicket(
    callback: (data: {
      ticketId: number;
      ticketNumber: string;
      subject: string;
      category: string;
      priority: string;
      user: { uuid: string; email: string };
      timestamp: Date;
    }) => void
  ): void {
    socketService.on('ticket:new', callback);
  }

  /**
   * Stop listening for new ticket notifications
   */
  offNewTicket(): void {
    socketService.off('ticket:new');
  }
}

// Export singleton instance
export const ticketService = new TicketService();
export default ticketService;
